/* eslint-disable import/no-named-as-default-member */
/*  eslint-disable react/destructuring-assignment*/

import React, {
  forwardRef,
  Fragment,
  LegacyRef,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";

import { Button, Flex } from "@samskip/frontend-components";
import bwipjs from "bwip-js";
import { format } from "date-fns";
import { navigate, PageProps } from "gatsby";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";

import { DomesticTypes } from "@assets/types";
import { SpinnerPage } from "@components";
import DomesticService from "@services/DomesticService";

import LabelHeader from "./LabelHeader";
import "./pdfLabels.less";

interface LabelsProps extends PageProps {
  bookingNumber: string;
}

const PdfLabels = (props: LabelsProps) => {
  const { t } = useTranslation();
  const [booking, setBooking] = useState<DomesticTypes.DomesticBookingLabel>();

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  useEffect(() => {
    DomesticService.getBookingLabelInfo(props.bookingNumber).then(
      (booking: DomesticTypes.DomesticBookingLabel) => setBooking(booking)
    );
  }, []);

  return booking ? (
    <div className="bg-gray-300 h-full w-full min-h-screen flex flex-col items-center">
      <div className="p-1">
        <Flex gap="small" justifyContent="end">
          <Button
            className="bg-neutral-0"
            onClick={() => navigate(-1)}
            variant="secondary"
          >
            {t("LABEL_BACK")}
          </Button>
          <Button onClick={handlePrint}>{t("LABEL_PRINTBARCODES")}</Button>
        </Flex>
        <PrintLabels booking={booking} ref={componentRef} />
      </div>
    </div>
  ) : (
    <SpinnerPage />
  );
};

interface PrintLabelsProps {
  booking: DomesticTypes.DomesticBookingLabel;
}

const PrintLabels = forwardRef(
  (props: PrintLabelsProps, ref: LegacyRef<HTMLDivElement> | undefined) => {
    const [detailCount, setDetailCount] = useState<number>(0);
    useEffect(() => {
      const totalCount = props.booking.OrderLines.reduce(
        (count, current) => count + current.Packages.length,
        0
      );
      setDetailCount(totalCount);
    }, [props.booking]);
    let currentIndex = 0;
    return (
      <div
        className="print-landscape"
        ref={ref}
        style={{ margin: "0", padding: "0" }}
      >
        {props.booking.OrderLines.map((line) => {
          return line.Packages.map((pack) => {
            return (
              <Fragment key={pack.SSCC}>
                <div className="page-break" />
                <PrintLabel
                  bookingNumber={props.booking.BookingNumber ?? ""}
                  createDate={format(
                    props.booking.CreateDate
                      ? new Date(props.booking.CreateDate)
                      : new Date(),
                    "dd.MM.yyyy"
                  )}
                  currentPackage={++currentIndex}
                  description={line.Description}
                  itemCondition={line.ItemCondition}
                  receiver={props.booking.To}
                  sender={props.booking.From}
                  sscc={pack.SSCC}
                  totalPackages={detailCount}
                  unNumber={line.UNNumber}
                />
              </Fragment>
            );
          });
        })}
      </div>
    );
  }
);

PrintLabels.displayName = "PrintLabels";

interface PrintLabelProps {
  sender: DomesticTypes.DomesticBookingLabelParty;
  receiver: DomesticTypes.DomesticBookingLabelParty;
  bookingNumber: string;
  createDate: string;
  itemCondition: string;
  description: string;
  sscc: string;
  currentPackage: number;
  totalPackages: number;
  unNumber: string;
}

const PrintLabel = (props: PrintLabelProps): ReactElement => {
  useEffect(() => {
    try {
      // If the barCode starts with a Character, then it's not a SCC18 barcode
      // The return value is the canvas element

      if (/\D/.test(props.sscc)) {
        // If props.sscc contains non-digit characters, render a different barcode
        bwipjs.toCanvas(props.sscc, {
          bcid: "code128",
          text: props.sscc,
          scale: 3,
          height: 13,
          includetext: true,
          textxalign: "center",
        });
      } else {
        // If props.sscc contains only digits, render a SCC18 barcode
        bwipjs.toCanvas(props.sscc, {
          bcid: "sscc18",
          text: "(00)" + props.sscc,
          scale: 3,
          height: 13,
          includetext: true,
          textxalign: "center",
        });
      }
    } catch (e) {
      console.log(e);
    }
  }, []);
  return (
    <div
      className="px-4 py-1 bg-neutral-0 h-100 flex flex-col space-between mb-2"
      style={{ width: "709px", height: "350px", color: "black" }}
    >
      <LabelHeader
        postCode={props.receiver.PostalCode}
        type={props.itemCondition}
        unNumber={props.unNumber}
      />
      <div className="flex justify-between flex-grow my-1 leading-6">
        <div className="flex flex-col w-1/3 px-1 truncate">
          <h4 className="font-bold">Móttakandi:</h4>
          <span>{props.receiver.Name}</span>
          <span>{props.receiver.Street}</span>
          <span>
            {props.receiver.PostalCode} {props.receiver.City}
          </span>
          <span>{props.receiver.PhoneNumber}</span>
        </div>
        <div className="flex flex-col space-between border-r-2 border-l-2 border-black w-1/3 pl-1 truncate">
          <h4 className="font-bold">Sendandi:</h4>
          <span>{props.sender.Name}</span>
          <span>{props.sender.Street}</span>
          <span>
            {props.sender.PostalCode} {props.sender.City}
          </span>
          <span>{props.sender.PhoneNumber}</span>
        </div>
        <div className="flex flex-col w-1/3 px-1 truncate">
          <div>
            <div className="flex">
              <span className="font-bold">Bókunarnr. </span>
              {props.bookingNumber}
            </div>
            <div>
              <span className="font-bold">Dags/Tími </span>
              {props.createDate}
            </div>
            <div>
              <span className="font-bold">Miði/Alls </span>
              {props.currentPackage}/{props.totalPackages}
            </div>
          </div>
          <span>{props.description}</span>
          <span>{_.upperFirst(props.itemCondition.toLowerCase())}</span>
        </div>
      </div>
      <div className="flex justify-center border-t-2 border-black pt-1">
        <canvas id={props.sscc} />
      </div>
    </div>
  );
};

export default PdfLabels;
